import { useState } from 'react';
import { API_URL } from '@constants';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';

const ENDPOINT_URL = '/spaces';

export interface CreateSpaceInput {
    space_type_uuid: string;
    space_status_uuid: string;
    title: string;
    description: string;
    address: string;
    latitude: number;
    longitude: number;
    number_of_doors: number;
    number_of_floors: number;
    construction_year: number;
    certificate_energy_uuid: string;
    space_type: string;
    featured_type: string;
    is_active: boolean;
    area_m2: number;
    price: number;
}

export type CreateSpaceFormData = FormData;

export interface CreateSpaceSuccessResponse {
    status: number;
    success: boolean;
    message: string;
    data: {
        uuid: string;
        [key: string]: any;
    };
    links: Record<string, unknown>;
}

export interface SpaceValidationErrors {
    [key: string]: string[];
}

/**
 * Requests endpoint to create a new space.
 */
export const useCreateSpace = () => {
    const { getHeaders, handleCommonErrors } = useApiConfig();
    const [loading, setLoading] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<SpaceValidationErrors | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<number | null>(null);
    const [createdSpace, setCreatedSpace] = useState<CreateSpaceSuccessResponse | null>(null);

    const request = async (input: CreateSpaceInput | FormData) => {
        setLoading(true);
        setSuccess(false);
        setErrorMessage(null);
        setValidationErrors(null);

        try {
            const isFormData = input instanceof FormData;
            const headers = getHeaders();

            // Remove Content-Type for FormData - browser will set it with boundary
            if (isFormData) {
                delete (headers as Record<string, string>)['Content-Type'];
            }

            const response = await fetch(`${API_URL}${ENDPOINT_URL}`, {
                method: 'POST',
                headers,
                body: isFormData ? input : JSON.stringify(input),
            });

            const json = await response.json();

            if (!response.ok) {
                const error = new Error(json.message || json.error || 'Erro ao criar espaço') as ApiError;
                error.statusCode = response.status;

                if (response.status === 422 && json.errors) {
                    setValidationErrors(json.errors);
                } else {
                    setErrorMessage(json.message || json.error || 'Erro ao criar espaço');
                }

                throw error;
            }

            const data: CreateSpaceSuccessResponse = json;
            setSuccess(true);
            setCreatedSpace(data);
            setErrorMessage(null);
            setValidationErrors(null);

            return data;
        } catch (err: any) {
            setSuccess(false);
            handleCommonErrors(err);
        } finally {
            setLoading(false);
            setLastResponseTime(Date.now());
        }
    };

    return {
        request,
        loading,
        success,
        errorMessage,
        validationErrors,
        lastResponseTime,
        createdSpace,
    };
};
